import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import {
  MAX_CONTENT_WIDTH,
  ROUTE_CREATE_CROP_ORDER,
  ROUTE_CROP_CUSTOMER_ORDERS,
  ROUTE_CROP_HOME,
} from 'appConstants';
import { Wizard } from 'components';
import { useAddresses } from 'contexts/Addresses';
import { useCropOrders } from 'contexts/CropOrders';
import { useProducts } from 'contexts/Products';
import { useNotification } from 'shared/Notification';

import CustomerDetails from './CustomerDetails';
import useCustomerSchema from './CustomerDetails/useValidationSchema';
import OrderProducts from './OrderProducts';
import useProductsValidationSchema from './OrderProducts/useValidationSchema';
import OrderSummary from './OrderSummary';
import type { ICropOrderWizard } from './types';

const defaultStepProps = {
  containerGridProps: { maxWidth: MAX_CONTENT_WIDTH },
  actionsGridProps: { width: MAX_CONTENT_WIDTH + 16 },
};

export default function CropOrderWizard({
  routePath,
  onCancel,
  initialFormValues,
  setTouchedOnMount,
}: ICropOrderWizard) {
  const customerSchema = useCustomerSchema();
  const productsSchema = useProductsValidationSchema();
  const navigate = useNavigate();
  const { notification } = useNotification();

  const {
    actions: { resetAddresses },
  } = useAddresses();
  const {
    state: { orderSaving },
    actions: { saveOrder },
  } = useCropOrders();

  const {
    actions: { resetProducts },
  } = useProducts();

  const { t } = useTranslation('CreateCropOrder');

  const redirectToOrder = useCallback(
    (customerId) => {
      navigate(`${ROUTE_CROP_HOME}/${ROUTE_CROP_CUSTOMER_ORDERS}/${customerId}`);
    },
    [navigate],
  );

  const handleSubmit = useCallback(
    (values) => {
      const { customer: { customerReference = '' } = {} } = values;
      saveOrder(
        values,
        () => {
          resetProducts();
          redirectToOrder(customerReference);
        },
        () => {
          notification(t('errors.unableToCreateOrder'), 'error');
        },
      );
    },
    [t, notification, redirectToOrder, saveOrder, resetProducts],
  );

  const { internalOrderId = '' } = initialFormValues;

  useEffect(
    () => () => {
      resetAddresses();
    },
    [resetAddresses],
  );

  return (
    <Wizard
      key={internalOrderId}
      steps={[
        {
          ...defaultStepProps,
          component: <CustomerDetails />,
          path: '/',
          validationSchema: customerSchema,
        },
        {
          ...defaultStepProps,
          component: <OrderProducts />,
          path: '/products',
          validationSchema: productsSchema,
        },
        {
          ...defaultStepProps,
          component: <OrderSummary />,
          path: '/summary',
          containerGridProps: { lg: 10, sm: 12, maxWidth: 849 },
        },
      ]}
      isSaving={orderSaving}
      initialFormValues={initialFormValues}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      routePath={routePath}
      submitLabel={t('buttons.submitOrder')}
      setTouchedOnMount={setTouchedOnMount}
      promptData={{
        title: t('dialogs.unsavedChanges.title'),
        message: t('dialogs.unsavedChanges.description'),
        onActionLabel: t('dialogs.unsavedChanges.buttonPrimary'),
        baseExcludedPath: `${ROUTE_CROP_HOME}/${ROUTE_CREATE_CROP_ORDER}`,
      }}
    />
  );
}
